<script lang="ts">
	import {
		Icon,
		MarkdownToolbar,
		MarkdownPreview,
		MarkdownTextarea,
		MarkdownImageUploadManager,
		MarkdownImageUploadProgress,
		MarkdownLinkModal
	} from '@repo/commons/components'
	import { generateId } from '@repo/commons/utils'
	import { onMount, onDestroy } from 'svelte'
	import { DEFAULT_TOOLBAR_CONFIG } from '@repo/commons/utils'

	import type {
		UploadedImage,
		MarkdownInputProps,
		KeyboardShortcut,
		HistoryEntry,
		EditorMode
	} from '@repo/commons/models'
	import {
		handleKeyboardShortcuts,
		saveToHistory,
		undoHistory,
		redoHistory,
		restoreCursorPosition,
		createDebouncer,
		insertNumberedListItem,
		insertBulletListItem,
		SAVE_DELAY
	} from './markdownInputUtils'

	let {
		label = '',
		helperText = '',
		value = $bindable(''),
		defaultMode = 'edit',
		placeholder = 'Start typing...',
		errorMessage = '',
		pure = false,
		showError = false,
		readonly = false,
		disabled = false,
		name,
		id,
		className = '',
		maxHeight = '300px',
		minHeight = '100px',
		fitHeight = false,
		enableImageUpload = true,
		acceptedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
		maxImageSize = 10,
		toolbarConfig = DEFAULT_TOOLBAR_CONFIG,
		children,
		allowLineBreaks = true,
		onInput = () => {},
		onBlur = () => {}
	}: MarkdownInputProps = $props()

	const defaultId = id ?? generateId()

	let textareaComponentRef = $state<MarkdownTextarea>()
	let imageUploadManagerRef = $state<MarkdownImageUploadManager>()
	let markdownMode = $state<EditorMode>(defaultMode)

	let history = $state<HistoryEntry[]>([])
	let historyIndex = $state(-1)
	let isUndoRedoing = $state(false)
	let showLinkDialog = $state(false)
	let selectedText = $state('')

	const debouncedSaveToHistory = createDebouncer(SAVE_DELAY)

	const isPreviewMode = $derived.by(() => markdownMode === 'preview')
	const canUndo = $derived.by(() => historyIndex > 0)
	const canRedo = $derived.by(() => historyIndex < history.length - 1)

	function handleInput(newValue: string) {
		value = newValue
		onInput(value)

		if (!isUndoRedoing) {
			debouncedSaveToHistory(() => {
				const textareaElement = textareaComponentRef?.textareaRef
				const result = saveToHistory(history, historyIndex, value, textareaElement)
				history = result.newHistory
				historyIndex = result.newIndex
			})
		}
	}

	function handleValueChange(newValue: string) {
		value = newValue
		onInput(value)
	}

	function performUndo() {
		const textareaElement = textareaComponentRef?.textareaRef
		if (!textareaElement) return

		isUndoRedoing = true
		const result = undoHistory(history, historyIndex, textareaElement)

		if (result.entry) {
			historyIndex = result.newIndex
			handleValueChange(result.entry.content)
			restoreCursorPosition(textareaElement, result.entry.selectionStart, result.entry.selectionEnd)
		}

		setTimeout(() => {
			isUndoRedoing = false
		}, 0)
	}

	function performRedo() {
		const textareaElement = textareaComponentRef?.textareaRef
		if (!textareaElement) return

		isUndoRedoing = true
		const result = redoHistory(history, historyIndex, textareaElement)

		if (result.entry) {
			historyIndex = result.newIndex
			handleValueChange(result.entry.content)
			restoreCursorPosition(textareaElement, result.entry.selectionStart, result.entry.selectionEnd)
		}

		setTimeout(() => {
			isUndoRedoing = false
		}, 0)
	}

	function handleKeyDown(event: KeyboardEvent) {
		const textareaElement = textareaComponentRef?.textareaRef
		if (!textareaElement) return

		const shortcuts: KeyboardShortcut[] = [
			{
				key: 'z',
				ctrlKey: true,
				shiftKey: true,
				action: performRedo
			},
			{
				key: 'z',
				metaKey: true,
				shiftKey: true,
				action: performRedo
			},
			{
				key: 'z',
				ctrlKey: true,
				action: performUndo
			},
			{
				key: 'z',
				metaKey: true,
				action: performUndo
			},
			{
				key: 'y',
				ctrlKey: true,
				action: performRedo
			},
			{
				key: 'y',
				metaKey: true,
				action: performRedo
			},
			{
				key: 'k',
				ctrlKey: true,
				action: handleLinkShortcut
			},
			{
				key: 'k',
				metaKey: true,
				action: handleLinkShortcut
			},
			{
				key: 's',
				ctrlKey: true,
				shiftKey: true,
				action: () => insertText('~~', '~~')
			},
			{
				key: 's',
				metaKey: true,
				shiftKey: true,
				action: () => insertText('~~', '~~')
			}
		]

		handleKeyboardShortcuts(event, shortcuts)
	}

	function handlePaste(event: ClipboardEvent) {
		if (!enableImageUpload || readonly || disabled || !event.clipboardData) return

		const items = Array.from(event.clipboardData.items)
		const imageItems = items.filter((item) => item.type.startsWith('image/'))

		if (imageItems.length === 0) return

		event.preventDefault()

		const textareaElement = textareaComponentRef?.textareaRef
		if (textareaElement) {
			const historyResult = saveToHistory(history, historyIndex, value, textareaElement)
			history = historyResult.newHistory
			historyIndex = historyResult.newIndex
		}

		imageItems.forEach((item) => {
			const file = item.getAsFile()
			if (file) {
				imageUploadManagerRef?.processClipboardImage(file)
			}
		})
	}

	function insertText(before: string, after: string = '') {
		if (readonly || disabled || !textareaComponentRef) return

		const textareaElement = textareaComponentRef.textareaRef
		if (!textareaElement) return

		const historyResult = saveToHistory(history, historyIndex, value, textareaElement)
		history = historyResult.newHistory
		historyIndex = historyResult.newIndex

		textareaComponentRef.insertText(before, after)
	}

	function insertNumberedList() {
		if (readonly || disabled || !textareaComponentRef) return

		const textareaElement = textareaComponentRef.textareaRef
		if (!textareaElement) return

		const historyResult = saveToHistory(history, historyIndex, value, textareaElement)
		history = historyResult.newHistory
		historyIndex = historyResult.newIndex

		const result = insertNumberedListItem(value, textareaElement)
		value = result.newValue
		onInput(value)

		// Set cursor position
		requestAnimationFrame(() => {
			if (textareaElement) {
				textareaElement.focus()
				textareaElement.setSelectionRange(result.cursorPosition, result.cursorPosition)
			}
		})
	}

	function insertBulletList() {
		if (readonly || disabled || !textareaComponentRef) return

		const textareaElement = textareaComponentRef.textareaRef
		if (!textareaElement) return

		const historyResult = saveToHistory(history, historyIndex, value, textareaElement)
		history = historyResult.newHistory
		historyIndex = historyResult.newIndex

		const result = insertBulletListItem(value, textareaElement)
		value = result.newValue
		onInput(value)

		// Set cursor position
		requestAnimationFrame(() => {
			if (textareaElement) {
				textareaElement.focus()
				textareaElement.setSelectionRange(result.cursorPosition, result.cursorPosition)
			}
		})
	}

	function handleImageSelect() {
		if (!enableImageUpload || readonly || disabled) return
		imageUploadManagerRef?.triggerFileSelect()
	}

	function handlePreviewClick() {
		markdownMode = isPreviewMode ? 'edit' : 'preview'
	}

	function handleLinkShortcut() {
		if (readonly || disabled || isPreviewMode) return
		handleLinkClick()
	}

	function handleLinkClick() {
		if (readonly || disabled || isPreviewMode) return

		const textareaElement = textareaComponentRef?.textareaRef
		if (!textareaElement) return

		// Get selected text but don't save to history yet - we'll do that in handleLinkConfirm
		const start = textareaElement.selectionStart
		const end = textareaElement.selectionEnd
		selectedText = value.substring(start, end)

		showLinkDialog = true
	}

	function handleLinkConfirm(linkText: string, url: string) {
		showLinkDialog = false

		const textareaElement = textareaComponentRef?.textareaRef
		if (!textareaElement) return

		// Preserve scroll position before any changes
		const scrollTop = textareaElement.scrollTop
		const linkMarkdown = `[${linkText}](${url})`

		if (selectedText) {
			// Use the new replaceSelectedText method which properly handles input
			textareaComponentRef?.replaceSelectedText(linkMarkdown)
		} else {
			// Insert at cursor position using the insertText method which handles history
			textareaComponentRef?.insertText(linkMarkdown)
		}

		// Ensure scroll position is maintained after a short delay
		setTimeout(() => {
			if (textareaElement) {
				textareaElement.scrollTop = scrollTop
			}
		}, 100)

		selectedText = ''
	}

	function handleLinkCancel() {
		showLinkDialog = false
		selectedText = ''

		// Return focus to textarea
		setTimeout(() => {
			textareaComponentRef?.textareaRef?.focus()
		}, 0)
	}

	function handleImageInsert(imageMarkdown: string) {
		const textareaElement = textareaComponentRef?.textareaRef
		const cursorPos = textareaElement?.selectionStart ?? value.length
		value = value.substring(0, cursorPos) + imageMarkdown + value.substring(cursorPos)
		onInput(value)
	}

	function handleImageRemove(filename: string, url: string) {
		// Remove image markdown from the content
		const imagePattern = new RegExp(
			`!\\[${filename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]\\(${url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\)`,
			'g'
		)
		value = value.replace(imagePattern, '')
		onInput(value)
	}

	function handleUploadComplete(imageData: UploadedImage & { url?: string }) {
		// Replace preview URL with final URL in markdown
		if (imageData.previewUrl) {
			value = value.replace(imageData.previewUrl, imageData.url)
			onInput(value)
		}
	}

	function handleUploadError(error: string, filename: string) {
		console.error(`Image upload failed for ${filename}: ${error}`)
	}

	function handleRetryUpload(id: string) {
		imageUploadManagerRef?.retryUpload(id)
	}

	function handleRemoveImage(id: string) {
		imageUploadManagerRef?.removeImage(id)
	}

	onDestroy(() => {
		imageUploadManagerRef?.cleanup()
	})

	onMount(() => {
		if (value) {
			const textareaElement = textareaComponentRef?.textareaRef
			const result = saveToHistory(history, historyIndex, value, textareaElement)
			history = result.newHistory
			historyIndex = result.newIndex
		}
	})
</script>

<div>
	<label for={defaultId} class="label-wrapper">
		{#if label}
			<span class="label">
				{label}
			</span>
		{/if}
		{#if helperText}
			<p class="helper-text">
				{helperText}
			</p>
		{/if}

		<div class={['editor-wrapper', className]} class:invalid={showError} class:disabled class:pure>
			{#if !readonly && !disabled}
				<MarkdownToolbar
					{isPreviewMode}
					{enableImageUpload}
					{toolbarConfig}
					{canUndo}
					{canRedo}
					onPreviewClick={handlePreviewClick}
					onInsertText={insertText}
					onImageSelect={handleImageSelect}
					onUndo={performUndo}
					onRedo={performRedo}
					onLink={handleLinkClick}
					onNumberedList={insertNumberedList}
					onBulletList={insertBulletList}
				/>
			{/if}

			<!-- Editor Content -->
			<div class="editor-content" class:fit-height={fitHeight} onpaste={handlePaste}>
				{#if isPreviewMode}
					<MarkdownPreview {pure} content={value} />
				{:else}
					<MarkdownTextarea
						bind:this={textareaComponentRef}
						id={defaultId}
						bind:value
						{placeholder}
						{readonly}
						{disabled}
						{name}
						{maxHeight}
						{minHeight}
						{showError}
						onInput={handleInput}
						{onBlur}
						onKeyDown={handleKeyDown}
						onInsertText={() => {}}
						{allowLineBreaks}
					/>
				{/if}

				{#if children}
					<div class="bottom-content">
						{@render children()}
					</div>
				{/if}
			</div>

			<!-- Upload Progress -->
			{#if enableImageUpload && imageUploadManagerRef?.uploadedImages}
				<MarkdownImageUploadProgress
					uploadedImages={imageUploadManagerRef.uploadedImages}
					onRetryUpload={handleRetryUpload}
					onRemoveImage={handleRemoveImage}
				/>
			{/if}
		</div>
	</label>

	{#if showError}
		<div class="error-message">
			<Icon name="fieldError" class="text-red-400" />
			<p class="error-text">{errorMessage}</p>
		</div>
	{/if}

	{#if enableImageUpload}
		<MarkdownImageUploadManager
			bind:this={imageUploadManagerRef}
			enabled={enableImageUpload}
			acceptedTypes={acceptedImageTypes}
			maxSize={maxImageSize}
			{readonly}
			{disabled}
			onImageInsert={handleImageInsert}
			onImageRemove={handleImageRemove}
			onUploadComplete={handleUploadComplete}
			onUploadError={handleUploadError}
		/>
	{/if}

	<MarkdownLinkModal
		isOpen={showLinkDialog}
		{selectedText}
		onConfirm={handleLinkConfirm}
		onCancel={handleLinkCancel}
	/>
</div>

<style>
	.label-wrapper {
		@apply block;
	}
	.label {
		@apply text-base text-[#001626E5] leading-8;
	}
	.helper-text {
		@apply text-sm text-[#001D33B2];
	}
	.editor-wrapper {
		@apply bg-white border-[1px] border-solid border-[#002C4D80] relative flex flex-col w-full rounded-lg overflow-hidden shadow-sm mt-2;
	}
	.editor-wrapper.disabled {
		@apply bg-gray-100;
	}
	.editor-wrapper:focus-within {
		@apply outline outline-2 outline-brand outline-offset-2;
	}
	.editor-wrapper.invalid {
		@apply border-[#E4485B] bg-[#E4485B]/5;
	}
	.editor-wrapper.pure {
		@apply border-none shadow-none;
	}
	.editor-content {
		@apply flex flex-col flex-1 min-h-[100px];
	}
	.editor-content.fit-height {
		min-height: 0;
	}
	.error-message {
		@apply flex items-center min-h-8 mb-1 text-red-400;
	}
	.error-text {
		@apply text-[#E4485B] text-sm font-semibold ml-2;
	}
</style>
